import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, fmtQty } from "../lib/api";
import { haptic } from "../lib/mobile";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Repeat, CalendarDays } from "lucide-react";
import { toast } from "sonner";

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const load = () => api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const buyAgain = async (o) => {
    try {
      await api.post("/orders", {
        symbol: o.symbol, asset_type: o.asset_type,
        side: "buy", quantity: o.quantity, route: o.route || "sim",
      });
      haptic("buy");
      toast.success(`Re-ordered ${o.quantity} ${o.ticker}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reorder failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="orders-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">History</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-1">Orders</h1>
        </div>
        <Link to="/history" data-testid="link-to-journal"
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground border border-border rounded-full px-3 py-1.5 transition-colors">
          <CalendarDays className="w-4 h-4" /> View by day
        </Link>
      </div>

      {orders.length ? (
        <Card className="rounded-xl border-border overflow-hidden">
          <div className="hidden sm:grid grid-cols-12 text-xs uppercase tracking-wider text-muted-foreground px-4 py-3 bg-secondary/40">
            <div className="col-span-2">Date</div>
            <div className="col-span-3">Asset</div>
            <div className="col-span-1">Side</div>
            <div className="col-span-2 text-right">Qty</div>
            <div className="col-span-1 text-right">Price</div>
            <div className="col-span-1 text-right">Comm.</div>
            <div className="col-span-2 text-right">Total · Route</div>
          </div>
          <div className="divide-y divide-border">
            {orders.map((o) => (
              <div key={o.id} className="grid grid-cols-2 sm:grid-cols-12 gap-y-1 items-center px-4 py-3 hover:bg-secondary/40" data-testid={`order-row-${o.id}`}>
                <div className="hidden sm:block sm:col-span-2 text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString()} <span className="opacity-60">{new Date(o.created_at).toLocaleTimeString()}</span></div>
                <div className="sm:col-span-3">
                  <div className="font-medium text-sm">{o.ticker}</div>
                  <div className="text-xs text-muted-foreground">{o.name}</div>
                </div>
                <div className="col-start-2 row-start-1 sm:col-start-auto sm:row-start-auto sm:col-span-1 text-right sm:text-left">
                  <span className={`text-xs uppercase font-medium px-2 py-0.5 rounded-full ${o.side === "buy" ? "bg-[hsl(var(--gain))]/15 gain" : "bg-[hsl(var(--loss))]/15 loss"}`}>
                    {o.side}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground sm:col-span-2 sm:text-right sm:text-sm sm:text-foreground font-mono-tab">
                  <span className="sm:hidden">Qty </span>{fmtQty(o.quantity)}
                </div>
                <div className="hidden sm:block sm:col-span-1 text-right font-mono-tab text-sm">{fmtMoney(o.price)}</div>
                <div className="hidden sm:block sm:col-span-1 text-right font-mono-tab text-xs text-muted-foreground">{fmtMoney(o.commission || 0)}</div>
                <div className="col-span-2 sm:col-span-2 text-right flex items-center justify-end gap-2">
                  <div>
                    <div className="font-mono-tab text-sm font-medium">{fmtMoney(o.total)}</div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      {o.route === "ibkr_paper" ? "IBKR paper" : o.route === "ibkr_live" ? "IBKR live" : "Sim"}
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => buyAgain(o)} data-testid={`buy-again-${o.id}`}
                          title="Buy again with same qty and route"
                          className="rounded-full h-8 w-8 p-0 shrink-0">
                    <Repeat className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      ) : (
        <Card className="p-16 text-center border-border rounded-xl">
          <div className="text-sm text-muted-foreground">No orders yet.</div>
        </Card>
      )}
    </div>
  );
}
